import React, { useRef } from "react";
import emailjs from "emailjs-com";
import { useTranslation } from "react-i18next";

interface RequestQuoteProps {
    isArabic?: boolean;
}

const RequestQuote: React.FC<RequestQuoteProps> = ({}) => {
    const { t, i18n } = useTranslation();
    const isArabic = i18n.language === "ar";
    const formRef = useRef<HTMLFormElement>(null);

    const sendEmail = (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!formRef.current) return;

        emailjs
            .sendForm(
                "service_00s1hky",
                "template_v60579w",
                formRef.current,
                "RrI8_5Wz07X95nN9G",
            )
            .then(
                (result) => {
                    alert(
                        isArabic
                            ? "تم إرسال الطلب بنجاح!"
                            : "Quote request sent successfully!",
                    );
                    formRef.current?.reset();
                },
                (error) => {
                    alert(
                        isArabic
                            ? "فشل الإرسال. حاول مرة أخرى."
                            : "Failed to send. Please try again.",
                    );
                    console.error(error);
                },
            );
    };

    return (
        <section
            className="relative flex min-h-[700px] w-full items-center justify-center overflow-hidden py-20 px-4 sm:px-6 lg:px-8"
            id="quote-section"
            dir={isArabic ? "rtl" : "ltr"}
        >
            <img
                src="/cargo/requestQuote/background.png"
                alt="Logistics"
                className="absolute inset-0 h-full w-full object-cover"
            />
            
            {/* Elegant overlay gradient */}
            <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/40"></div>

            <div className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-12 lg:flex-row lg:items-center lg:gap-16">
                {/* Left Side: Info */}
                <div className="flex w-full flex-col justify-center text-white lg:w-1/2">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-4 py-1.5 w-fit backdrop-blur-sm border border-white/20">
                        <img
                            src="/cargo/herosection/right_arrow_color.svg"
                            alt="colored arrow"
                            className={`h-4 w-4 ${isArabic ? "rotate-180" : ""}`}
                        />
                        <span className="text-sm font-bold tracking-wider text-[#00B9EC] uppercase">
                            {isArabic ? "تواصل معنا" : "GET IN TOUCH"}
                        </span>
                    </div>

                    <h2 className="mb-6 text-4xl font-extrabold leading-tight sm:text-5xl lg:text-6xl text-white drop-shadow-lg">
                        {isArabic ? "دعنا نتولى" : "LET US HANDLE"}
                        <br />
                        <span className="text-transparent bg-clip-text bg-gradient-to-r from-[#00B9EC] to-blue-400">
                            {isArabic ? "أمور شحنتك" : "YOUR FREIGHT MATTERS"}
                        </span>
                    </h2>

                    <p className="max-w-xl text-lg text-slate-200">
                        {isArabic 
                            ? "نوفر حلول شحن موثوقة وآمنة تتناسب مع احتياجاتك. دعنا نهتم بالتفاصيل بينما تركز أنت على نمو أعمالك."
                            : "We provide reliable and secure shipping solutions tailored to your needs. Let us handle the details while you focus on growing your business."}
                    </p>
                </div>

                {/* Right Side: Quote Form */}
                <div className="w-full lg:w-1/2 flex justify-center lg:justify-end">
                    <div className="relative w-full max-w-lg overflow-hidden rounded-3xl border border-white/20 bg-white/10 p-8 shadow-2xl backdrop-blur-xl sm:p-10">
                        <form
                            ref={formRef}
                            onSubmit={sendEmail}
                            className="flex flex-col space-y-5"
                        >
                            <div>
                                <h3 className="mb-2 text-2xl font-bold text-white sm:text-3xl">
                                    {isArabic ? "اطلب عرض سعر" : "Get a Quote"}
                                </h3>
                                <p className="mb-6 text-sm text-slate-300">
                                    {isArabic
                                        ? "أخبرنا عن شحنتك واحصل على عرض سعر مخصص."
                                        : "Tell us about your shipment and receive a personalized quote."}
                                </p>
                            </div>

                            <div className="space-y-4">
                                <input
                                    type="text"
                                    name="user_name"
                                    placeholder={isArabic ? "الاسم" : "Your Name"}
                                    required
                                    className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-slate-400 focus:border-[#00B9EC] focus:outline-none focus:ring-1 focus:ring-[#00B9EC] transition-colors"
                                />
                                <input
                                    type="email"
                                    name="user_email"
                                    placeholder={isArabic ? "البريد الإلكتروني" : "Your Email"}
                                    required
                                    className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-slate-400 focus:border-[#00B9EC] focus:outline-none focus:ring-1 focus:ring-[#00B9EC] transition-colors"
                                />
                                <input
                                    type="tel"
                                    name="user_phone"
                                    placeholder={isArabic ? "رقم الهاتف" : "Your Number"}
                                    required
                                    className="w-full rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-slate-400 focus:border-[#00B9EC] focus:outline-none focus:ring-1 focus:ring-[#00B9EC] transition-colors"
                                />
                                <textarea
                                    name="message"
                                    placeholder={isArabic ? "رسالتك" : "Your Message"}
                                    className="w-full resize-none rounded-xl border border-white/20 bg-white/5 px-4 py-3 text-white placeholder-slate-400 focus:border-[#00B9EC] focus:outline-none focus:ring-1 focus:ring-[#00B9EC] transition-colors"
                                    rows={4}
                                    required
                                />
                            </div>
                            <input
                                type="hidden"
                                name="recipient_email"
                                value="info@cargooapp.com"
                            />

                            <button
                                type="submit"
                                className="group relative mt-2 flex w-full items-center justify-center gap-3 overflow-hidden rounded-xl bg-[#00B9EC] px-8 py-3.5 font-bold text-white shadow-lg transition-transform hover:-translate-y-0.5 hover:shadow-[#00B9EC]/30"
                            >
                                <span className="relative z-10">{isArabic ? "إرسال الطلب" : "GET A QUOTE"}</span>
                                <div className="absolute inset-0 h-full w-full bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:animate-[shimmer_1.5s_infinite]"></div>
                            </button>
                        </form>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default RequestQuote;
